import Component from '@glimmer/component';
import { service } from '@ember/service';
import Chip from 'tio-reimagined/components/chip';
import { Section } from 'tio-ui/components/layout';
import Text from 'tio-reimagined/components/text';
import { t } from 'ember-intl';
import { eq } from 'tio-ui/utilities';
import {
  getVisibleFields,
  getGroupedVisibleFields,
} from '../../../utils/tuition-assistance/financial-details-config.ts';
import type TasApplicationModel from '../../../models/tas-application.ts';
import type { LimitsStatusDataSignature } from '../../../services/tuition-assistance.ts';
import type IntlService from 'ember-intl/services/intl';

export interface FinancialDetailsAdminSummarySignature {
  Args: {
    application: TasApplicationModel;
    limitsData?: LimitsStatusDataSignature;
  };
  Element: HTMLElement;
}

export default class FinancialDetailsAdminSummary extends Component<FinancialDetailsAdminSummarySignature> {
  @service declare intl: IntlService;

  get visibleFields() {
    return getVisibleFields(this.args.application, this.args.limitsData);
  }

  get groupedFields() {
    return getGroupedVisibleFields(this.args.application, this.args.limitsData);
  }

  get pcsCard() {
    return (
      this.args.application.pcsCards?.find((card) => card.isActive) ??
      this.args.application.pcsCards?.[0]
    );
  }

  get pcsCardState() {
    return this.pcsCard?.state;
  }

  get pcsCardStatusLabel() {
    switch (this.pcsCardState) {
      case 'PCS.CardState.ACTIVATED':
        return 'Active';
      case 'PCS.CardState.CREATED':
        return 'Pending Activation';
      case 'PCS.CardState.CANCELED':
        return 'Canceled';
      case 'PCS.CardState.INACTIVATED':
        return 'Inactive';
      default:
        return 'Inactive';
    }
  }

  get pcsCardStatusIntent() {
    switch (this.pcsCardState) {
      case 'PCS.CardState.ACTIVATED':
        return 'success';
      case 'PCS.CardState.CREATED':
        return 'warning';
      case 'PCS.CardState.CANCELED':
      case 'PCS.CardState.INACTIVATED':
      default:
        return 'default';
    }
  }

  get pcsCardStatusDotClass() {
    switch (this.pcsCardState) {
      case 'PCS.CardState.ACTIVATED':
        return 'bg-emerald-500';
      case 'PCS.CardState.CREATED':
        return 'bg-amber-500';
      case 'PCS.CardState.CANCELED':
      case 'PCS.CardState.INACTIVATED':
      default:
        return 'bg-gray-400';
    }
  }

  formatFieldValue = (
    value: number | null | undefined,
    isPercentage = false,
    isCredits = false
  ) => {
    if (value === null || value === undefined) {
      return this.intl.t('common.not_applicable');
    }

    const normalizedValue = Number(value);
    if (Number.isNaN(normalizedValue)) {
      return this.intl.t('common.not_applicable');
    }

    if (isPercentage) {
      return `${normalizedValue.toFixed(0)}%`;
    }

    if (isCredits) {
      return `${normalizedValue} ${this.intl.t('common.credits')}`;
    }

    return this.intl.formatNumber(normalizedValue / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  };

  <template>
    <Section class="" data-test-financial-details-admin-summary>
      <:body>
        <div class="max-w-3xl">
          <div
            class="flex items-center justify-between px-4 py-2.5 bg-gray-100/50 dark:bg-gray-800/50 border border-gray-200/80 dark:border-gray-700/80 rounded-[10px]"
          >
            <Text class="text-base font-semibold text-gray-900">
              {{t "common.tas.financial_details_summary.title"}}
            </Text>
          </div>

          {{#each this.groupedFields as |group|}}
            <div class="mt-4" data-test-financial-section={{group.section}}>
              <div class="px-4 py-2 bg-gray-50 border-b border-gray-200">
                <div class="flex items-center justify-between gap-3">
                  <Text class="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {{t group.labelKey}}
                  </Text>
                  {{#if (eq group.section "course-cash-details")}}
                    <Chip
                      @appearance="faded"
                      @intent={{this.pcsCardStatusIntent}}
                      @size="sm"
                      data-test-course-cash-card-status
                    >
                      <span class="inline-flex items-center gap-1">
                        <span
                          class="h-1.5 w-1.5 rounded-full {{this.pcsCardStatusDotClass}}"
                          aria-hidden="true"
                        ></span>
                        <span>{{this.pcsCardStatusLabel}}</span>
                      </span>
                    </Chip>
                  {{/if}}
                </div>
              </div>
              {{#each group.fields as |field|}}
                <div
                  class="flex py-4 px-4 border-b border-gray-200 last:border-b-0"
                  data-test-financial-details-field={{field.key}}
                >
                  <div class="w-64 md:w-72 shrink-0">
                    <Text class="text-sm font-medium text-gray-600 md:whitespace-nowrap">
                      {{t field.labelKey}}
                    </Text>
                  </div>
                  <div class="flex-1">
                    <Text class="text-sm font-medium text-gray-700 pl-6">
                      {{this.formatFieldValue field.value field.isPercentage field.isCredits}}
                    </Text>
                  </div>
                </div>
              {{/each}}
            </div>
          {{/each}}
        </div>
      </:body>
    </Section>
  </template>
}
